import React, { useEffect, useState, useTransition } from 'react';
import type { ListingStatsFilterState } from '../../components/admin/listingManagement/ListingStatsFilters';
import { cityService } from '../../api/services/CityService';
import { topCategoryService } from '../../api/services/TopCategoryService';
import { statusService } from '../../api/services/StatusService';
import { secondaryCategoryService } from '../../api/services/SecondaryCategoryService';
import { productTypeService } from '../../api/services/ProductTypeService';
import { listingService } from '../../api/services/ListingService';
import ListingStatsFilters from '../../components/admin/listingManagement/ListingStatsFilters';
import ListingStatsChartWrapper from '../../components/admin/listingManagement/ListingStatsChartWrapper';
import type { TopCategoryViewModel } from '../../api/types/top-category';
import type { CityViewModel } from '../../api/types/city';
import type { StatusViewModel } from '../../api/types/status';
import type { SecondaryCategoryViewModel } from '../../api/types/secondary-category';
import type { ProductTypeViewModel } from '../../api/types/product-type';
import { ListingSortOption } from 'api/utils/listingMapper';
import AdListFlatItem from 'components/AdList/AdListFlatItem';
import { usePendingListings } from 'hooks/usePendingListings';
import { useListingApproval } from 'hooks/useListingApproval';
import { AdListing } from 'types/adListing';
import MultiSelectModal, { MultiSelectModalWithChildren } from 'components/Modal/MultiSelectModal';
import AdminPendingReelsSection from 'components/admin/reels/AdminPendingReelsSection';
import type { ListingDetailProduct } from 'api/utils/listingDetailMapper';
import { listingViewModelToDetailProduct, getSpecIcon, getSpecLabel } from 'api/utils/listingDetailMapper';
import type { ListingViewModel } from 'api/types/listings/ListingViewModel';
async function loadStatuses(): Promise<StatusViewModel[]> {
  const res = await statusService.getAll({});
  return res.isSuccess && res.data ? res.data : [];
}

const SORT_OPTIONS: { value: ListingSortOption; label: string }[] = [
  { value: 'newest', label: 'En Yeni Tarih' },
  { value: 'oldest', label: 'En Eski Tarih' },
  { value: 'priceAsc', label: 'En Düşük Fiyat' },
  { value: 'priceDesc', label: 'En Yüksek Fiyat' },
];

const ListingManagementPage: React.FC = () => {
  const [filterState, setFilterState] = useState<ListingStatsFilterState>({});
  const [isPending, startTransition] = useTransition();
  const [topCategories, setTopCategories] = useState<TopCategoryViewModel[]>([]);
  const [secondaryCategories, setSecondaryCategories] = useState<SecondaryCategoryViewModel[]>([]);
  const [productTypes, setProductTypes] = useState<ProductTypeViewModel[]>([]);
  const [cities, setCities] = useState<CityViewModel[]>([]);
  const [statuses, setStatuses] = useState<StatusViewModel[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedListing, setSelectedListing] = useState<AdListing | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    topCategoryService.getAll().then((r) => {
      if (r.isSuccess && r.data) setTopCategories(r.data);
    });
    secondaryCategoryService.getAll({}).then((r) => {
      if (r.isSuccess && r.data) setSecondaryCategories(r.data);
    });
    productTypeService.getAll({}).then((r) => {
      if (r.isSuccess && r.data) setProductTypes(r.data);
    });
    cityService.getAll({ pagination: { pageSize: 500, pageNumber: 1 } }).then((r) => {
      if (r.isSuccess && r.data) setCities(r.data);
    });
    loadStatuses().then(setStatuses);
  }, []);

  const setFilterStateTransition = (updater: React.SetStateAction<ListingStatsFilterState>) => {
    startTransition(() => {
      setFilterState(updater);
    });
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pb-2 mb-3 border-bottom">
        <h1 className="h2">İlan Yönetimi</h1>
      </div>
      {modalOpen && selectedListing && (
        <PendingListingModal
          listing={selectedListing}
          setModalOpen={setModalOpen}
          setSelectedListing={setSelectedListing}
          onActionComplete={() => setRefreshTrigger((c) => c + 1)}
        />
      )}
      <ListingStatsFilters
        filterState={filterState}
        setFilterState={setFilterStateTransition}
        topCategories={topCategories}
        secondaryCategories={secondaryCategories}
        productTypes={productTypes}
        cities={cities}
        statuses={statuses}
      />
      {isPending && (
        <div className="text-muted small mb-2">
          <span className="spinner-border spinner-border-sm me-1" role="status" />
          Filtre güncelleniyor...
        </div>
      )}
      <div className="row">
        <div className="col-12">
          <ListingStatsChartWrapper filterState={filterState} refreshTrigger={refreshTrigger} />
        </div>
      </div>
      <PendingListings setSelectedListing={setSelectedListing} setModalOpen={setModalOpen} refreshTrigger={refreshTrigger} />
    </>
  );
};

export default ListingManagementPage;

/* ─────────────────────── PendingListings ─────────────────────── */

interface PendingListingsProps {
  setSelectedListing: (listing: AdListing) => void;
  setModalOpen: (open: boolean) => void;
  refreshTrigger: number;
}

function PendingListings({ setSelectedListing, setModalOpen, refreshTrigger }: PendingListingsProps) {
  const {
    listings,
    totalPages,
    currentPage,
    loading,
    fetchError,
    sortOption,
    setSortOption,
    fetchListings,
    setCurrentPage,
  } = usePendingListings();

  useEffect(() => {
    if (refreshTrigger > 0) fetchListings();
  }, [refreshTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleListingClick = (listing: AdListing) => {
    setSelectedListing(listing);
    setModalOpen(true);
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortOption(e.target.value as ListingSortOption);
  };

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const pageNumbers = buildPageNumbers(currentPage, totalPages);

  return (
    <div className="row" style={{ marginTop: '20px' }}>
      <div className="col-12">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <h5 className="mb-0">
            <i className="fas fa-clock me-2 text-warning" />
            Onay Bekleyen İlanlar
          </h5>
          <div className="d-flex align-items-center flex-shrink-0">
            <label htmlFor="pendingSortSelect" className="me-2 text-nowrap text-muted small">
              Sıralama:
            </label>
            <select
              id="pendingSortSelect"
              className="form-select form-select-sm"
              value={sortOption}
              onChange={handleSortChange}
              style={{ width: 'auto' }}
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {loading && (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status" />
            <p className="text-muted mt-2 mb-0">Yükleniyor...</p>
          </div>
        )}

        {fetchError && (
          <div className="alert alert-danger">{fetchError}</div>
        )}

        {!loading && !fetchError && listings.length === 0 && (
          <div className="text-center py-4 text-muted">
            <i className="fas fa-check-circle fa-2x mb-2" />
            <p className="mb-0">Onay bekleyen ilan bulunmuyor.</p>
          </div>
        )}

        {!loading && listings.length > 0 && (
          <>
            <div className="list-view">
              {listings.map((ilan) => (
                <div key={ilan.id} onClick={() => handleListingClick(ilan)} style={{ cursor: 'pointer' }}>
                  <AdListFlatItem
                    ilan={ilan}
                    disableDetailLinks
                    onToggleFavorite={() => {}}
                    displayFavorites={false}
                  />
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <nav className="d-flex justify-content-center mt-4">
                <ul className="pagination mb-0">
                  <li className={`page-item ${currentPage <= 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => goToPage(currentPage - 1)}>
                      <i className="fas fa-chevron-left" />
                    </button>
                  </li>
                  {pageNumbers.map((p, idx) =>
                    p === '...' ? (
                      <li key={`ellipsis-${idx}`} className="page-item disabled">
                        <span className="page-link">...</span>
                      </li>
                    ) : (
                      <li key={p} className={`page-item ${currentPage === p ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => goToPage(p as number)}>
                          {p}
                        </button>
                      </li>
                    )
                  )}
                  <li className={`page-item ${currentPage >= totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => goToPage(currentPage + 1)}>
                      <i className="fas fa-chevron-right" />
                    </button>
                  </li>
                </ul>
              </nav>
            )}

            <div className="text-center text-muted small mt-2">
              Sayfa <strong>{currentPage}</strong> / {totalPages}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Build a compact array of page numbers with ellipsis for large ranges. */
function buildPageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages: (number | '...')[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);

  if (start > 2) pages.push('...');
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < total - 1) pages.push('...');
  pages.push(total);

  return pages;
}

/* ─────────────────────── PendingListingModal ─────────────────────── */

interface PendingListingModalProps {
  listing: AdListing;
  setModalOpen: (open: boolean) => void;
  setSelectedListing: (listing: AdListing | null) => void;
  onActionComplete: () => void;
}

function PendingListingModal({ listing, setModalOpen, setSelectedListing, onActionComplete }: PendingListingModalProps) {
  const { approveListing, rejectListing, loading: approvalLoading, error: approvalError } = useListingApproval();
  const [detail, setDetail] = useState<ListingDetailProduct | null>(null);
  const [listingVm, setListingVm] = useState<ListingViewModel | null>(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [grantTestBadgeOnApprove, setGrantTestBadgeOnApprove] = useState(false);
  const [markTestedLoading, setMarkTestedLoading] = useState(false);
  const [markTestedError, setMarkTestedError] = useState<string | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<'approve' | 'reject' | null>(null);
  const [rejectionReasonDraft, setRejectionReasonDraft] = useState('');

  useEffect(() => {
    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    setListingVm(null);
    setGrantTestBadgeOnApprove(false);
    setMarkTestedError(null);
    setRejectionReasonDraft('');
    listingService.getById(String(listing.id)).then((result) => {
      if (cancelled) return;
      if (result.isSuccess && result.data) {
        setListingVm(result.data);
        setDetail(listingViewModelToDetailProduct(result.data));
      } else {
        setDetailError(result.message ?? 'İlan detayı yüklenemedi.');
      }
      setDetailLoading(false);
    }).catch((err) => {
      if (cancelled) return;
      setDetailError(err instanceof Error ? err.message : 'Bir hata oluştu.');
      setDetailLoading(false);
    });
    return () => { cancelled = true; };
  }, [listing.id]);

  const closeModal = () => {
    setModalOpen(false);
    setSelectedListing(null);
    setRejectionReasonDraft('');
  };

  const showTestBadgeCheckbox =
    listingVm?.appliedForTesting === true && listingVm.isTested !== true;

  const executeApprove = async () => {
    setMarkTestedError(null);
    const result = await approveListing(String(listing.id));
    if (!result.isSuccess) return;

    if (showTestBadgeCheckbox && grantTestBadgeOnApprove) {
      setMarkTestedLoading(true);
      try {
        const testedRes = await listingService.makeListingTested(String(listing.id));
        const httpErr = (testedRes.statusCode ?? 0) >= 400;
        if (!testedRes.isSuccess && httpErr) {
          setMarkTestedError(testedRes.message ?? 'Test Edildi rozeti verilemedi.');
          onActionComplete();
          return;
        }
      } finally {
        setMarkTestedLoading(false);
      }
    }

    onActionComplete();
    closeModal();
  };

  const executeReject = async () => {
    const result = await rejectListing(String(listing.id), rejectionReasonDraft);
    if (result.isSuccess) {
      onActionComplete();
      closeModal();
    }
  };

  const handleFooterApproveClick = () => {
    setRejectionReasonDraft('');
    setPendingConfirm('approve');
  };
  const handleFooterRejectClick = () => setPendingConfirm('reject');

  const handleConfirmAction = () => {
    const action = pendingConfirm;
    setPendingConfirm(null);
    if (action === 'approve') void executeApprove();
    else if (action === 'reject') void executeReject();
  };

  const handleCancelConfirm = () => {
    setPendingConfirm(null);
    setRejectionReasonDraft('');
  };

  const detailReady = !detailLoading && !detailError && detail != null;
  const actionsDisabled =
    !detailReady || approvalLoading || markTestedLoading;

  return (
    <>
      {pendingConfirm !== null && (
        <MultiSelectModal
          title=""
          message="Emin misiniz?"
          onAccept={handleConfirmAction}
          onReject={handleCancelConfirm}
          onDismiss={handleCancelConfirm}
          stackOnTop
        >
          {pendingConfirm === 'reject' ? (
            <div>
              <label htmlFor="confirmRejectReason" className="form-label small text-muted mb-1">
                Reddetme gerekçesi (isteğe bağlı)
              </label>
              <textarea
                id="confirmRejectReason"
                className="form-control"
                rows={3}
                maxLength={2000}
                value={rejectionReasonDraft}
                onChange={(e) => setRejectionReasonDraft(e.target.value)}
                placeholder="Satıcıya gösterilecek açıklama; boş bırakılırsa varsayılan metin kullanılır."
              />
            </div>
          ) : null}
        </MultiSelectModal>
      )}
    <MultiSelectModalWithChildren
      onAccept={handleFooterApproveClick}
      onReject={handleFooterRejectClick}
      acceptText="Onayla"
      rejectText="Reddet"
      dialogClassName="modal-xl modal-fullscreen-sm-down modal-dialog-scrollable"
      onDismiss={closeModal}
      acceptDisabled={actionsDisabled}
      rejectDisabled={actionsDisabled}
    >
      <div className="text-start">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <h5 className="mb-0">{detail?.title ?? listing.baslik ?? 'İlan detayı'}</h5>
          <button
            type="button"
            className="btn-close"
            onClick={closeModal}
            aria-label="Kapat"
          />
        </div>

      {detailLoading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" />
          <p className="text-muted mt-2 mb-0">İlan detayı yükleniyor...</p>
        </div>
      )}

      {detailError && (
        <div className="alert alert-danger">{detailError}</div>
      )}

      {detailReady && detail && (
        <div>

          <div className="row">
            {listingVm?.ownerProfile && (
              <div className="col-12 mb-3">
                <div className="card border-0 bg-light">
                  <div className="card-body py-3">
                    <h6 className="text-muted text-uppercase small mb-3">İlan sahibi</h6>
                    <div className="d-flex align-items-start gap-3">
                      {listingVm.ownerProfile.avatarUrl ? (
                        <img
                          src={listingVm.ownerProfile.avatarUrl}
                          alt=""
                          className="rounded-circle"
                          width={56}
                          height={56}
                          style={{ objectFit: 'cover' }}
                        />
                      ) : (
                        <div
                          className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white flex-shrink-0"
                          style={{ width: 56, height: 56 }}
                        >
                          <i className="fas fa-user" aria-hidden />
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="fw-semibold">{listingVm.ownerProfile.displayName}</div>
                        {listingVm.ownerProfile.subtitle ? (
                          <div className="text-muted small text-break">{listingVm.ownerProfile.subtitle}</div>
                        ) : null}
                        {listingVm.ownerProfile.email ? (
                          <div className="small mt-1">
                            <span className="text-muted">E-posta: </span>
                            {listingVm.ownerProfile.email}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Image gallery */}
            <div className="col-md-6 mb-3">
              <div className="position-relative mb-2">
                <img
                  src={detail.images[activeImageIndex]}
                  alt={detail.title}
                  className="img-fluid rounded w-100"
                  style={{ maxHeight: '360px', objectFit: 'contain', backgroundColor: 'var(--sb-bg-muted)' }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23f0f0f0" width="400" height="300"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle"%3ENo image%3C/text%3E%3C/svg%3E';
                  }}
                />
                {detail.images.length > 1 && (
                  <>
                    <button
                      type="button"
                      className="btn btn-sm btn-light position-absolute top-50 start-0 translate-middle-y ms-1 rounded-circle shadow-sm"
                      onClick={() => setActiveImageIndex((prev) => (prev - 1 + detail.images.length) % detail.images.length)}
                    >
                      <i className="fas fa-chevron-left" />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-light position-absolute top-50 end-0 translate-middle-y me-1 rounded-circle shadow-sm"
                      onClick={() => setActiveImageIndex((prev) => (prev + 1) % detail.images.length)}
                    >
                      <i className="fas fa-chevron-right" />
                    </button>
                  </>
                )}
              </div>
              {detail.images.length > 1 && (
                <div className="d-flex gap-2 overflow-auto">
                  {detail.images.map((img, idx) => (
                    <img
                      key={idx}
                      src={img}
                      alt={`${detail.title} ${idx + 1}`}
                      className={`rounded border ${idx === activeImageIndex ? 'border-primary border-2' : ''}`}
                      style={{ width: '60px', height: '60px', objectFit: 'cover', cursor: 'pointer' }}
                      onClick={() => setActiveImageIndex(idx)}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ))}
                </div>
              )}
              {showTestBadgeCheckbox && (
                <div className="form-check text-start mt-3 mb-0">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="grantTestBadgeOnApprove"
                    checked={grantTestBadgeOnApprove}
                    onChange={(e) => setGrantTestBadgeOnApprove(e.target.checked)}
                  />
                  <label className="form-check-label small" htmlFor="grantTestBadgeOnApprove">
                    Test Edildi Olarak İşaretle
                  </label>
                </div>
              )}
            </div>

            {/* Details */}
            <div className="col-md-6 mb-3">
              <div className="mb-3">
                <span className="h4 text-primary fw-bold">
                  {detail.price.toLocaleString('tr-TR')} ₺
                </span>
              </div>

              <div className="d-flex flex-wrap gap-2 mb-3">
                {detail.conditionName && (
                  <span className="badge bg-info text-dark">
                    <i className="fas fa-tag me-1" />
                    {detail.conditionName}
                  </span>
                )}
                {detail.productTypeName && (
                  <span className="badge bg-secondary">
                    <i className="fas fa-laptop me-1" />
                    {detail.productTypeName}
                  </span>
                )}
                {(detail.location.city || detail.location.district) && (
                  <span className="badge bg-light text-dark border">
                    <i className="fas fa-map-marker-alt me-1" />
                    {[detail.location.district, detail.location.city].filter(Boolean).join(', ')}
                  </span>
                )}
                {detail.viewCount != null && (
                  <span className="badge bg-light text-dark border">
                    <i className="fas fa-eye me-1" />
                    {detail.viewCount} görüntülenme
                  </span>
                )}
              </div>

              {/* Specs */}
              {Object.keys(detail.specs).length > 0 && (
                <div className="card mb-3">
                  <div className="card-body py-2 px-3">
                    <h6 className="card-title mb-2">
                      <i className="fas fa-laptop-code me-2 text-primary" />
                      Özellikler
                    </h6>
                    <div className="row g-2">
                      {Object.entries(detail.specs).map(([key, value]) => (
                        <div key={key} className="col-6">
                          <div className="d-flex align-items-center">
                            <i className={`fas fa-${getSpecIcon(key)} text-primary me-2`} />
                            <div>
                              <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                                {getSpecLabel(key)}
                              </div>
                              <div className="fw-medium small">{value}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Description */}
              <div className="card">
                <div className="card-body py-2 px-3">
                  <h6 className="card-title mb-2">
                    <i className="fas fa-align-left me-2 text-primary" />
                    Açıklama
                  </h6>
                  <p className="small mb-0" style={{ maxHeight: '120px', overflowY: 'auto' }}>
                    {detail.description || 'Açıklama belirtilmemiş.'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <AdminPendingReelsSection
            listingId={String(listing.id)}
            title="İlana Bağlı Onay Bekleyen Gönderiler"
          />

          {(approvalLoading || markTestedLoading) && (
            <div className="text-center text-muted small mt-2">
              <span className="spinner-border spinner-border-sm me-1" role="status" />
              İşlem gerçekleştiriliyor...
            </div>
          )}

          {approvalError && (
            <div className="alert alert-danger small mt-2 mb-0">{approvalError}</div>
          )}

          {markTestedError && (
            <div className="alert alert-warning small mt-2 mb-0">{markTestedError}</div>
          )}
        </div>
      )}
      </div>
    </MultiSelectModalWithChildren>
    </>
  );
}
